import { preloadMlDsa } from "../crypto/ml_dsa";
import { i18next } from "../i18n";
import {
  formatServerError,
  resolveBackendError,
  tryParseBackendError,
} from "../lib/format/server_error";
import { reachable } from "../lib/format/network_error";

/**
 * Account API wrappers — `POST /accounts/login` + `POST
 * /accounts/signup`.
 *
 * The flow is gated by the caller's explicit `mode`:
 *   - `"login"` (default) — login ONLY. A 401 (wrong password for an
 *     existing username, or no such account) surfaces the real
 *     `auth.login_failed` message; it NEVER silently falls back to
 *     signup. Falling back used to mask a wrong-password 401 as a
 *     signup 409 "username already taken", which is the wrong surface
 *     for a user who simply mistyped their password.
 *   - `"signup"` — the user explicitly chose to register, so the
 *     helper keeps the "try-login-then-signup" affordance: a fresh
 *     username creates the account; an existing username with the
 *     SAME password logs straight in (idempotent re-register), and a
 *     password mismatch surfaces the `auth.username_taken` conflict.
 *
 * The server enforces a minimum password length of 8 characters
 * (`arkhe-holdem-server/src/routes.rs::accounts_signup`); the client
 * silently pads shorter passwords with a fixed suffix so signup
 * doesn't fail on short test credentials — the user only ever sees
 * their original input.
 */

const PASSWORD_PAD = "_arkhe_pad_8";
const PASSWORD_MIN_LEN = 8;

export interface LoginResult {
  accountId: number;
  /** The username as it was signed up — sign-in ignores case, so this,
   *  not what was typed, is the name to show. */
  username: string;
  playerIdHex: string;
  signingKeyHex: string;
  /** Free off-table balance from the BE wallet (`chip_balance`
   *  column). The login response splits this from the held
   *  aggregate so the FE never has to derive "total =
   *  free + sum(holds)" from a single number — the cap-style
   *  wallet display reads both fields directly. */
  chipFree: number;
  /** Sum of active wallet holds (chips currently locked behind
   *  open table buy-ins). `0` when the player is not seated at
   *  any table. Pre-bump server peers omit the field; the parser
   *  defaults to `0` so the cap-style "total = free + held"
   *  composition still produces the right number. */
  /** Chips at tables right now — the sum of live seat stacks. */
  chipInPlay: number;
  chipHoldTotal: number;
  /** Account row's stored locale preference. `null` when the row
   *  never set one; the negotiation chain in `src/i18n/index.ts`
   *  falls through to localStorage / navigator / ko-KR fallback.
   *  Forward-compat: a pre-bump server omits the field, the
   *  response parser hands `null` through. */
  locale: string | null;
  /** CSRF Double-Submit / Synchronizer Token (ADR-010).
   *  Hex-encoded value bound to the
   *  session-cookie row the same response set; the auth slice
   *  stashes this for `authedRequest` to echo back on every
   *  mutating fetch via the `x-csrf-token` header. */
  csrfToken: string;
  /** The account's second signing leg, for an account that turned on
   *  quantum-safe sign-in: the ML-DSA seed the server keeps sealed under
   *  the password, and its credential epoch. `null` otherwise. */
  mlDsa: { seedHex: string; epoch: number } | null;
}

export type AuthFlowMode = "login" | "signup";

export interface LoginRequestArgs {
  serverBase: string;
  username: string;
  password: string;
  /** Caller-explicit intent. `"login"` (default) NEVER falls back to
   *  signup — a 401 surfaces `auth.login_failed`. `"signup"` keeps the
   *  try-login-then-signup register affordance. */
  mode?: AuthFlowMode;
  fetchImpl?: typeof fetch;
}

/** The password as the server knows it. Short passwords are padded to
 *  the server's minimum, so every route that checks a password has to be
 *  sent the same padded form. */
export function padPassword(password: string): string {
  return password.length >= PASSWORD_MIN_LEN
    ? password
    : password + PASSWORD_PAD;
}

export async function signupOrLogin(
  args: LoginRequestArgs,
): Promise<LoginResult> {
  const fetchImpl = reachable(args.fetchImpl ?? fetch);
  const mode: AuthFlowMode = args.mode ?? "login";
  const padded = padPassword(args.password);

  const tryLogin = async () =>
    fetchImpl(`${args.serverBase}/accounts/login`, {
      method: "POST",
      // The BE arms the
      // session cookie via `Set-Cookie` on this response, so the
      // browser needs `credentials: 'include'` to actually persist
      // the header. Without it the cookie lands but isn't echoed
      // back on subsequent fetches, defeating the entire session
      // path. Mirrors PokerStars / GGPoker / OWASP §1.2.3 default.
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ username: args.username, password: padded }),
    });

  let response = await tryLogin();
  // Signup fallback fires ONLY when the user explicitly chose to
  // register. A login-mode 401 (wrong password / no such account)
  // falls straight through to the `auth.login_failed` surface below
  // instead of masquerading as a signup 409 "username taken".
  if (response.status === 401 && mode === "signup") {
    // Signup body carries credentials only — the BE owns the
    // starting chip balance via a server-side constant so a
    // tampered client cannot mint chips at signup. Sending an
    // `initial_chip_balance` field used to clamp at
    // `MAX_SIGNUP_BONUS` server-side; the BE has since switched
    // to `deny_unknown_fields`, so the field would reject the
    // request outright. Keeping the wire surface minimal also
    // closes the structural "client picks the bonus" stance the
    // FE used to broadcast.
    const signupResponse = await fetchImpl(
      `${args.serverBase}/accounts/signup`,
      {
        method: "POST",
        // Match the login arm — signup doesn't issue the session
        // cookie itself (the follow-up `tryLogin` does) but
        // keeping `credentials: 'include'` consistent across the
        // pair makes the fetch surface auditable.
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          username: args.username,
          password: padded,
        }),
      },
    );
    if (signupResponse.status === 409) {
      throw new Error(
        i18next.t("auth.username_taken", { username: args.username }),
      );
    }
    if (!signupResponse.ok) {
      const body = await signupResponse.text().catch(() => "");
      // A rule the form states (username / password) is the player's to
      // fix, so it reads as the rule alone — no "signup failed (400)".
      const envelope = tryParseBackendError(body);
      if (envelope?.i18n_key.startsWith("signup.") === true) {
        throw new Error(resolveBackendError(envelope));
      }
      throw new Error(formatServerError(signupResponse.status, body));
    }
    response = await tryLogin();
  }

  if (!response.ok) {
    // A login-mode 401 is a wrong password (or no such account):
    // surface the explicit "login failed — check your username or
    // password" message instead of the generic "auth required" copy
    // (and never the signup 409 "username taken" surface, which the
    // removed auto-fallback used to leak).
    if (response.status === 401 && mode === "login") {
      throw new Error(i18next.t("auth.login_failed"));
    }
    const body = await response.text().catch(() => "");
    throw new Error(formatServerError(response.status, body));
  }
  const data = (await response.json()) as {
    account_id: number;
    username: string;
    ed25519_vk: string;
    signing_key: string;
    chip_balance: number;
    /** Pre-bump server peers omit the field — default to 0 so a
     *  stale server still produces a valid cap-style snapshot
     *  (total degrades to free, never crashes). */
    in_play_chips?: number;
    held_chips?: number;
    locale?: string | null;
    /** The current BE always emits this field. Older server peers
     *  that predate the session-cookie + CSRF model omit it; an
     *  empty string flows
     *  through here so the helper layer surfaces a 403
     *  `auth.csrf_invalid` on the first mutating call, which the
     *  caller treats as "stale BE, force re-login". */
    csrf_token?: string;
    ml_dsa_seed?: string;
    ml_dsa_epoch?: number;
  };
  const mlDsa =
    typeof data.ml_dsa_seed === "string" &&
    typeof data.ml_dsa_epoch === "number"
      ? { seedHex: data.ml_dsa_seed, epoch: data.ml_dsa_epoch }
      : null;
  // Every request from here on carries the ML-DSA leg, and the signer
  // that produces it is synchronous over a lazily-loaded module. Load it
  // before the session exists rather than on the first request.
  if (mlDsa !== null) await preloadMlDsa();
  return {
    accountId: data.account_id,
    username: data.username,
    playerIdHex: data.ed25519_vk,
    signingKeyHex: data.signing_key,
    chipFree: data.chip_balance,
    chipInPlay: data.in_play_chips ?? 0,
    chipHoldTotal: data.held_chips ?? 0,
    locale: data.locale ?? null,
    csrfToken: data.csrf_token ?? "",
    mlDsa,
  };
}
